/**
 * A Team's pick list: what Bailey reads from the tracker, in her order, from
 * `GET /api/desks/:id/pick-list`. That route is the proposal lane's own
 * backlog read, so the list here is the list Bailey picks from, not a second
 * reading of the filter. What Bailey sees prints all of it; Add tickets
 * prints the top of it to pick from.
 *
 * `PickListBasis` says why these tickets and no others: the backlog as
 * written (never paraphrased), whose tickets, and what Bailey always skips.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { TeamPickList, TeamPickListBacklog } from "@yagni/shared";
import { OpenInNewIcon } from "../queue/index.js";
import { captureUiError } from "../sentry.js";
import { checkedWords } from "./BacklogPreview.js";

export const PICK_LIST_SKIPS =
  "Bailey skips closed and started tickets, epics, sub-tasks, and tickets a Team already holds.";
/** The same rule on a Team Bailey does not pick for: what the list leaves out. */
export const PICK_LIST_LEFT_OUT =
  "Left out: closed and started tickets, epics, sub-tasks, and tickets a Team already holds.";

const TRACKER_WORD = { jira: "Jira", linear: "Linear" } as const;

export interface PickListState {
  list: TeamPickList | null;
  loading: boolean;
  failed: boolean;
  /** Read the tracker again now, past the two-minute answer. */
  recheck: () => void;
}

/**
 * `enabled` false skips the read (a Team Bailey does not pick for); a new
 * `revision` (the filter as written) reads again, so the list follows a save.
 */
export function usePickList(
  deskId: string,
  enabled = true,
  revision: string | null = null,
): PickListState {
  const [list, setList] = useState<TeamPickList | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [fresh, setFresh] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setList(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    const url = `/api/desks/${encodeURIComponent(deskId)}/pick-list${fresh > 0 ? "?fresh=1" : ""}`;
    fetch(url, { credentials: "include" })
      .then((res) =>
        res.ok
          ? (res.json() as Promise<TeamPickList>)
          : Promise.reject(new Error(String(res.status))),
      )
      .then((body) => {
        if (!cancelled) setList(body);
      })
      .catch((err: unknown) => {
        captureUiError(err, { area: "Teams", operation: "pick_list_read", deskId });
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [deskId, fresh, enabled, revision]);

  const recheck = useCallback(() => setFresh((n) => n + 1), []);
  return { list, loading, failed, recheck };
}

/**
 * Opens the ticket itself in a new tab, so a person can check it in the
 * tracker. Inside a row's label it never toggles the pick.
 */
export function TicketLink({
  ticket,
}: {
  ticket: { source: "jira" | "linear"; identifier: string; url?: string | null };
}) {
  if (!ticket.url) return <span aria-hidden />;
  const where = TRACKER_WORD[ticket.source];
  return (
    <a
      className="tlink"
      href={ticket.url}
      target="_blank"
      rel="noopener noreferrer"
      title={`Open in ${where}`}
      aria-label={`Open ${ticket.identifier} in ${where}`}
      onClick={(event) => event.stopPropagation()}
    >
      <OpenInNewIcon />
    </a>
  );
}

/** The trackers the list reads: its backlogs, and the tickets it holds. */
function trackersRead(list: TeamPickList): Array<"jira" | "linear"> {
  const sources = new Set<"jira" | "linear">([
    ...list.backlogs.map((b) => b.source),
    ...list.next.map((t) => t.source),
    ...list.onTeam.map((t) => t.source),
  ]);
  return (["jira", "linear"] as const).filter((source) => sources.has(source));
}

/** What an admin authorizes in each tracker: Jira projects, Linear teams. */
const AUTHORIZED_UNIT = { jira: "Jira projects", linear: "Linear teams" } as const;

/**
 * Where to go when the list is missing tickets. A private Jira project or
 * Linear team reaches YAGNI only once it is authorized, which an admin does
 * under Connections, on that tracker's "Who can see what". Saving a backlog
 * authorizes up to five of its Jira projects on its own; the rest wait
 * there. Names only the trackers this list reads, so a Linear Team never
 * reads "Jira".
 */
function AuthorizeHint({ list }: { list: TeamPickList }) {
  const read = trackersRead(list);
  const where = read.length === 1 ? `Connections › ${TRACKER_WORD[read[0]!]}` : "Connections";
  const units =
    read.length > 0
      ? read.map((source) => AUTHORIZED_UNIT[source]).join(" or ")
      : "projects or teams";
  const connections = <Link to="/services">{where}</Link>;
  const hidden = list.hiddenFromViewer;
  if (hidden > 0) {
    return (
      <p className="fact" style={{ margin: 0 }} data-testid="pick-list-hidden">
        {hidden} more {hidden === 1 ? "ticket sits" : "tickets sit"} in {units} your tracker access
        does not show you, so {hidden === 1 ? "it is" : "they are"} left off here. An admin can
        authorize them under {connections}.
      </p>
    );
  }
  return (
    <p className="fact" style={{ margin: 0 }} data-testid="pick-list-authorize">
      Not seeing the right tickets? An admin can authorize more {units} for YAGNI under{" "}
      {connections}.
    </p>
  );
}

/** Why a pick list holds nothing: the one ladder the sheet and the header both read. */
type EmptyReason =
  | { kind: "unreadable" }
  | { kind: "taken"; count: number }
  | { kind: "hidden" }
  | { kind: "empty" };

function emptyReasonOf(list: TeamPickList): EmptyReason {
  if (list.readFailed || list.backlogs.some((b) => b.status === "failed")) {
    return { kind: "unreadable" };
  }
  if (list.onTeam.length > 0) return { kind: "taken", count: list.onTeam.length };
  if (list.hiddenFromViewer > 0) return { kind: "hidden" };
  return { kind: "empty" };
}

/** Why a pick list holds nothing, in one sentence: taken, unreadable, or simply empty. */
export function pickListEmptySentence(list: TeamPickList): string {
  const reason = emptyReasonOf(list);
  switch (reason.kind) {
    case "unreadable":
      return "Nothing to show until the backlog can be read.";
    case "taken":
      return reason.count === 1
        ? "Nothing left: the one ticket in it is already on a Team."
        : `Nothing left: all ${reason.count} tickets in it are already on a Team.`;
    case "hidden":
      return "Nothing here you can see in the tracker.";
    case "empty":
      return "Nothing open in it right now.";
  }
}

/**
 * The queue header's short form of {@link pickListEmptySentence}, for a Team
 * whose Bailey picks on her own and finds nothing: "nothing to pick, all 3 are
 * already on a Team". Null while she has something to pick, or when she does
 * not pick on her own.
 */
export function nothingToPickLine(list: TeamPickList | null): string | null {
  if (!list || !list.picking || list.scope === "none" || list.next.length > 0) return null;
  const reason = emptyReasonOf(list);
  switch (reason.kind) {
    case "unreadable":
      return "nothing to pick until the backlog can be read";
    case "taken":
      return reason.count === 1
        ? "nothing to pick, its one ticket is already on a Team"
        : `nothing to pick, all ${reason.count} are already on a Team`;
    case "hidden":
      return "nothing to pick that you can see in the tracker";
    case "empty":
      return "nothing to pick, nothing open in it";
  }
}

/** The connected trackers' names, for a Team that attaches no backlog. */
function trackersWord(list: TeamPickList): string {
  const sources = new Set(list.next.map((t) => t.source));
  if (sources.size === 1) return TRACKER_WORD[[...sources][0]!];
  return "your tracker";
}

function ownersSentence(owners: TeamPickList["owners"]): string | null {
  if (!owners) return null;
  const who = [...owners.people, ...(owners.unassigned ? ["nobody (unassigned)"] : [])];
  if (who.length === 0)
    return "Only tickets assigned to people who have since left the Team, so nothing matches.";
  return `Only tickets assigned to ${who.join(", ")}.`;
}

function BacklogLine({ backlog }: { backlog: TeamPickListBacklog }) {
  return (
    <div className="blecho" data-testid="pick-list-backlog">
      <span className="label">
        {TRACKER_WORD[backlog.source]} · {backlog.label}
      </span>
      {backlog.query && <code>{backlog.query}</code>}
      {backlog.status === "failed" && (
        <div className="blst err" role="alert" style={{ marginTop: 6 }}>
          {backlog.failure ?? "This backlog could not be read."} Bailey reads nothing from it until
          it can.
        </div>
      )}
      {backlog.hidden != null && backlog.hidden > 0 && (
        <span className="fact" style={{ display: "block", marginTop: 4 }}>
          {backlog.hidden} ticket{backlog.hidden === 1 ? "" : "s"} in it sit in projects this Team
          cannot see, so Bailey leaves them out.
        </span>
      )}
    </div>
  );
}

/** Why these tickets and no others. */
export function PickListBasis({ list }: { list: TeamPickList }) {
  const owners = ownersSentence(list.owners);
  return (
    <div data-testid="pick-list-basis" style={{ display: "grid", gap: 8 }}>
      {list.scope === "none" && (
        <div className="blst warn">
          No tracker is connected, so Bailey has no tickets to read. Connect Jira or Linear in
          Connections.
        </div>
      )}
      {!list.picking && list.scope !== "none" && (
        <div className="blst quiet" data-testid="pick-list-by-hand">
          <b>Bailey does not pick for this Team.</b> It works only the tickets you add. These are
          open in {list.scope === "workspace" ? trackersWord(list) : "its backlog"}.
        </div>
      )}
      {list.picking && list.scope === "workspace" && (
        <div className="blst quiet" data-testid="pick-list-no-backlog">
          <b>Bailey reads every open ticket in {trackersWord(list)}.</b> A backlog in the
          Team&apos;s settings narrows it to a query, a view, or a project.
        </div>
      )}
      {list.readFailed && (
        <div className="blst err" role="alert" data-testid="pick-list-read-failed">
          Could not read the backlog just now, so this list may be missing tickets. Check again in a
          minute.
        </div>
      )}
      {list.backlogs.map((backlog, i) => (
        <BacklogLine key={`${backlog.kind}:${backlog.label}:${i}`} backlog={backlog} />
      ))}
      {list.scope !== "none" && <AuthorizeHint list={list} />}
      <p className="fact" style={{ margin: 0 }}>
        {owners ? `${owners} ` : ""}
        {list.picking ? PICK_LIST_SKIPS : PICK_LIST_LEFT_OUT}
        {list.orderedBy.length > 0
          ? ` In the order ${list.orderedBy.join(" and ")} sets, then most urgent.`
          : " Most urgent first."}
        {` Checked ${checkedWords(list.checkedAt)}.`}
      </p>
    </div>
  );
}
